import type { CasinoGameFindParams } from '../../types'
import type {
  SortTypeGames,
  CasinoGamesSortRule,
} from '../../composables/useSelectSorting'

interface CategoryPageReturnType {
  categoryCode: ComputedRef<string>
  subCategoryCode: ComputedRef<string>
  categoryTitle: ComputedRef<string>
  findParams: ComputedRef<CasinoGameFindParams>
  categoryTheme: Ref<number | 'all' | undefined>
  providerCode: Ref<string | undefined>
  isAllGamesForbidden: Ref<boolean>
  allowedByCountryOnly: Ref<boolean>
  sort: Ref<SortTypeGames>
  orderBy: ComputedRef<CasinoGamesSortRule[]>
  handleAllGamesForbidden: (isAllForbidden: boolean) => void
  handleZeroGamesFind: (total: number) => void
  gamesCount: Ref<number>
  totalGamesCount: Ref<number | null>
  unavailableGamesCount: ComputedRef<number>
  isFavorite: Ref<boolean>
}

interface CasinoFilters {
  sort: SortTypeGames
  categoryTheme: number | 'all' | undefined
  providerCode: string | undefined
  isFavorite: boolean
}

export function useCategoryPage(): CategoryPageReturnType {
  const { isMobile } = usePlatform()
  const route = useRoute()

  const filters = useSessionStorage<CasinoFilters>('casino-filters', {
    sort: 'weightInCategory',
    categoryTheme: undefined,
    providerCode: undefined,
    isFavorite: false,
  })

  const casinoStore = useCasinoStore()
  const { getCategoryByCode } = casinoStore

  const categoryCode = computed(() => route.params.categoryCode?.toString())
  const subCategoryCode = computed(
    () => route.params.subCategoryCode?.toString(),
  )
  const isAllCategory = computed(() => subCategoryCode.value === 'all')

  const categoryTheme = computed({
    get: () => filters.value.categoryTheme,
    set: (value) => {
      filters.value.categoryTheme = value
      return value
    },
  })
  const findParamCategoryId = computed<number[] | undefined>(() => {
    if (!categoryTheme.value) return undefined
    return categoryTheme.value === 'all' ? undefined : [categoryTheme.value]
  })
  const providerCode = computed({
    get: () => filters.value.providerCode,
    set: (value) => {
      filters.value.providerCode = value
      return value
    },
  })
  const allowedByCountryOnly = useLocalStorage<boolean>(
    'allowedByCountryOnly',
    false,
  )
  const findParams = computed<CasinoGameFindParams>(() => ({
    categoryCode: isAllCategory.value
      ? categoryCode.value
      : subCategoryCode.value,
    categoryId: findParamCategoryId.value,
    providerCode: providerCode.value === 'all' ? undefined : providerCode.value,
    allowedByCountryOnly: !allowedByCountryOnly.value,
    ...(isMobile.value ? { forMobile: true } : { forDesktop: true }),
  }))

  const category = computed(() =>
    getCategoryByCode(route.params.subCategoryCode.toString()),
  )
  const categoryTitle = computed(() => {
    if (isAllCategory.value) {
      return (
        getCategoryByCode(route.params.categoryCode.toString())?.labelName ||
        route.params.categoryCode.toString()
      )
    }
    return category.value?.name || route.params.subCategoryCode.toString()
  })
  const gamesCount = ref<number>(0)
  const isAllGamesForbidden = ref(false)
  function handleAllGamesForbidden(isAllForbidden: boolean) {
    isAllGamesForbidden.value = isAllForbidden
  }
  function handleZeroGamesFind(total: number) {
    gamesCount.value = total
    if (total === 0) {
      allowedByCountryOnly.value = true
    }
  }

  const { sort, orderBy } = useSelectSorting(filters.value.sort)

  watch(sort, () => {
    filters.value.sort = sort.value
  })

  const { data: totalGamesCount } = useStFetch('/casino/game/find', {
    method: 'post',
    lazy: true,
    server: false,
    body: {
      params: {
        ...findParams.value,
        forDesktop: isMobile.value ? undefined : true,
        forMobile: isMobile.value ? true : undefined,
        allowedByCountryOnly: false,
      },
      pagination: {
        page: 0,
        perPage: 0,
      },
    },
    default: () => 0,
    transform: (data) => data.paging.total ?? 0,
  })

  const { data: onlyAvailableGamesCount } = useStFetch('/casino/game/find', {
    method: 'post',
    lazy: true,
    server: false,
    body: {
      params: {
        ...findParams.value,
        forDesktop: isMobile.value ? undefined : true,
        forMobile: isMobile.value ? true : undefined,
        allowedByCountryOnly: true,
      },
      pagination: {
        page: 0,
        perPage: 0,
      },
    },
    default: () => 0,
    transform: (data) => data.paging.total ?? 0,
  })

  const unavailableGamesCount = computed(
    () => totalGamesCount.value - onlyAvailableGamesCount.value,
  )
  const isFavorite = computed({
    get: () => filters.value.isFavorite,
    set: (value) => {
      filters.value.isFavorite = value
      return value
    },
  })

  return {
    categoryCode,
    subCategoryCode,
    categoryTitle,
    findParams,
    categoryTheme,
    providerCode,
    isAllGamesForbidden,
    sort,
    orderBy,
    allowedByCountryOnly,
    handleAllGamesForbidden,
    handleZeroGamesFind,
    gamesCount,
    totalGamesCount,
    unavailableGamesCount,
    isFavorite,
  }
}
